import { type FC, useState, useMemo, useCallback, useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogBody,
    DialogFooter,
    DialogClose,
} from "@/components/podkit/dialog/Dialog";
import { Button } from "@/components/flexkit/Button";
import { RadioGroup, RadioGroupItem } from "@/components/podkit/forms/RadioListField";
import { Text } from "@/components/podkit/typography/Text";
import { type CalendarDate, today, getLocalTimeZone, startOfMonth } from "@internationalized/date";
import { DateRangePickerField } from "@/routes/settings/organization/enterprise-usage/DateRangePickerField";
import { TrackLocations } from "@/hooks/use-segment";

type DateRangeOption = "current-month" | "custom";

type ExportCsvModalProps = {
    open: boolean;
    onClose: () => void;
    onExport: (startDate: Date, endDate: Date) => void;
    isExporting: boolean;
};

function calendarDateToUTCDate(cd: CalendarDate): Date {
    return new Date(Date.UTC(cd.year, cd.month - 1, cd.day));
}

function calendarDateToUTCEndOfDay(cd: CalendarDate): Date {
    return new Date(Date.UTC(cd.year, cd.month - 1, cd.day, 23, 59, 59, 999));
}

function formatMonthRange(start: CalendarDate, end: CalendarDate): string {
    const startDate = calendarDateToUTCDate(start);
    const endDate = calendarDateToUTCDate(end);
    const startStr = startDate.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
    const endStr = endDate.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" });
    return `${startStr}\u2013${endStr}`;
}

export const ExportCsvModal: FC<ExportCsvModalProps> = ({ open, onClose, onExport, isExporting }) => {
    const todayDate = useMemo(() => today(getLocalTimeZone()), []);
    const monthStart = useMemo(() => startOfMonth(todayDate), [todayDate]);

    const [selectedOption, setSelectedOption] = useState<DateRangeOption>("current-month");
    const [customRange, setCustomRange] = useState<{ start: CalendarDate; end: CalendarDate }>({
        start: monthStart,
        end: todayDate,
    });

    const currentMonthLabel = useMemo(() => formatMonthRange(monthStart, todayDate), [monthStart, todayDate]);

    const handleExport = useCallback(() => {
        if (selectedOption === "current-month") {
            onExport(calendarDateToUTCDate(monthStart), calendarDateToUTCEndOfDay(todayDate));
        } else {
            onExport(calendarDateToUTCDate(customRange.start), calendarDateToUTCEndOfDay(customRange.end));
        }
    }, [selectedOption, monthStart, todayDate, customRange, onExport]);

    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    // Reset state when modal opens
    useEffect(() => {
        if (open) {
            setSelectedOption("current-month");
            setCustomRange({ start: monthStart, end: todayDate });
        }
    }, [open, monthStart, todayDate]);

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent data-track-location={TrackLocations.ExportCsvModal} className="overflow-visible">
                <DialogHeader>
                    <DialogTitle>Export CSV</DialogTitle>
                </DialogHeader>
                <DialogBody>
                    <Text className="mb-3 text-sm font-medium text-content-primary">Date range</Text>
                    <RadioGroup
                        value={selectedOption}
                        onValueChange={(v) => {
                            setSelectedOption(v as DateRangeOption);
                        }}
                        className="gap-3"
                    >
                        <label className="flex cursor-pointer items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <RadioGroupItem value="current-month" />
                                <Text className="text-sm text-content-primary">Current month</Text>
                            </div>
                            <Text className="text-sm text-content-secondary">{currentMonthLabel}</Text>
                        </label>

                        <div className="flex flex-col gap-2">
                            <label className="flex cursor-pointer items-center gap-3">
                                <RadioGroupItem value="custom" />
                                <Text className="text-sm text-content-primary">Custom</Text>
                            </label>
                            {selectedOption === "custom" && (
                                <div className="ml-7">
                                    <DateRangePickerField
                                        value={customRange}
                                        onChange={setCustomRange}
                                        maxValue={todayDate}
                                    />
                                </div>
                            )}
                        </div>
                    </RadioGroup>
                </DialogBody>
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="secondary" disabled={isExporting}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleExport}
                        loading={isExporting}
                        data-tracking-id="export-csv-confirm"
                    >
                        Export
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
